import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { apiFetch } from "../services/api";
import { User, Lead, LeadList, refreshAccount } from "../types";
import ContactDrawer from "../components/ContactDrawer";
import PublicNav, { PublicFooter } from "../components/PublicNav";
const fields = [
  ["job_title", "Job title", "e.g. Marketing Director"],
  ["company", "Company", "Company name"],
  ["industry", "Industry", "e.g. Technology"],
  ["country", "Country", "e.g. United States"],
  ["state", "State / region", "e.g. Texas"],
  ["city", "City", "e.g. Austin"],
  ["company_size", "Company size", "e.g. 51-200"],
  ["department", "Department", "e.g. Sales"],
];
export default function SearchPage({
  user,
  publicMode = false,
}: {
  user?: User;
  publicMode?: boolean;
}) {
  const [params, setParams] = useSearchParams(),
    navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>(
    Object.fromEntries(params),
  );
  const [leads, setLeads] = useState<Lead[]>([]),
    [total, setTotal] = useState(0),
    [pages, setPages] = useState(0),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const [selected, setSelected] = useState<number[]>([]),
    [drawer, setDrawer] = useState<Lead | null>(null),
    [confirm, setConfirm] = useState<Lead[]>([]),
    [busy, setBusy] = useState(false),
    [filterOpen, setFilterOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false),
    [searchName, setSearchName] = useState(""),
    [lists, setLists] = useState<LeadList[]>([]),
    [listId, setListId] = useState("");
  const page = Number(params.get("page") || 1),
    query = params.toString();
  useEffect(() => {
    setFilters(Object.fromEntries(new URLSearchParams(query)));
    setLoading(true);
    setError("");
    setSelected([]);
    let cancelled = false;
    apiFetch(
      `${publicMode ? "/api/public/leads" : "/api/leads/search"}?${query}`,
    )
      .then(async (r) => {
        const p = await r.json();
        if (!r.ok)
          throw new Error(
            typeof p.error === "string" ? p.error : "Search failed.",
          );
        if (!cancelled) {
          setLeads(p.leads);
          setTotal(p.total);
          setPages(p.totalPages);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, publicMode]);
  useEffect(() => {
    if (!publicMode)
      apiFetch("/api/workspace/lists")
        .then(async (r) => {
          if (r.ok) setLists((await r.json()).lists);
        })
        .catch(() => {});
  }, [publicMode, message]);
  const apply = (next = filters) => {
    setParams(
      new URLSearchParams(
        Object.entries({ ...next, page: "1" }).filter(([, v]) => v !== ""),
      ),
    );
    setFilterOpen(false);
  };
  const change = (key: string, value: string) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const requestUnlock = (items: Lead[]) => {
    if (publicMode) {
      navigate("/signup");
      return;
    }
    setConfirm(items.filter((l) => !l.unlocked));
  };
  const unlock = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await apiFetch("/api/leads/bulk-unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_ids: confirm.map((l) => l.id) }),
      });
      const p = await r.json();
      if (!r.ok) throw new Error(p.error || "Unable to unlock contacts.");
      const byId = new Map<number, Lead>(p.leads.map((l: Lead) => [l.id, l]));
      setLeads((old) => old.map((l) => byId.get(l.id) || l));
      if (drawer && byId.has(drawer.id)) setDrawer(byId.get(drawer.id)!);
      setMessage(
        `${p.leads.length} contact${p.leads.length === 1 ? "" : "s"} unlocked. Ready for your next step.`,
      );
      setSelected([]);
      setConfirm([]);
      refreshAccount();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to unlock.");
    }
    setBusy(false);
  };
  const saveSearch = async () => {
    setBusy(true);
    try {
      const { page: _, pageSize: __, ...clean } = Object.fromEntries(params);
      const r = await apiFetch("/api/workspace/searches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: searchName, filters: clean }),
      });
      const p = await r.json();
      if (!r.ok) throw new Error(p.error);
      setSaveOpen(false);
      setSearchName("");
      setMessage("Search saved. Find it in Saved searches.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save search.");
    }
    setBusy(false);
  };
  const addToList = async () => {
    if (!listId) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/api/workspace/lists/${listId}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_ids: selected }),
      });
      if (!r.ok) throw new Error();
      setMessage(
        "Contacts added to your list. Locked contacts stay locked until you unlock them.",
      );
      setSelected([]);
    } catch {
      setError("Unable to add contacts to your list.");
    }
    setBusy(false);
  };
  const content = (
    <>
      <div className="page-title">
        <div>
          <span className="eyebrow">DISCOVER YOUR NEXT CONNECTION</span>
          <h1>{publicMode ? "Find your people." : "Find leads"}</h1>
          <p>
            {publicMode
              ? "Explore a masked preview of real database records. Sign up to unlock contacts."
              : "A focused search today. A new opportunity tomorrow."}
          </p>
        </div>
        {!publicMode && (
          <button
            className="button button-outline"
            onClick={() => setSaveOpen(true)}
          >
            ☆ Save search
          </button>
        )}
      </div>
      <form
        className="workspace-search"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <span aria-hidden="true">⌕</span>
        <input
          aria-label="Search contacts"
          placeholder="Search a name, company, role, or keyword…"
          value={filters.keyword || ""}
          onChange={(e) => change("keyword", e.target.value)}
        />
        <button className="button button-dark" disabled={loading}>
          Search <span>→</span>
        </button>
      </form>
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
      {message && (
        <div className="notice" role="status">
          {message}
          <button aria-label="Dismiss message" onClick={() => setMessage("")}>
            ×
          </button>
        </div>
      )}
      <div className="search-workspace">
        <aside className={`search-filters ${filterOpen ? "mobile-open" : ""}`}>
          <div className="panel-heading">
            <h2>Filters</h2>
            <button
              className="text-button"
              onClick={() => {
                setFilters({});
                apply({});
              }}
            >
              Reset
            </button>
            <button
              className="mobile-close"
              onClick={() => setFilterOpen(false)}
              aria-label="Close filters"
            >
              ×
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
          >
            {fields.map(([key, label, placeholder]) => (
              <details
                key={key}
                open={
                  !!filters[key] ||
                  ["job_title", "industry", "country"].includes(key)
                }
              >
                <summary>
                  {label}
                  {filters[key] && <span className="filter-dot" />}
                </summary>
                <label className="sr-only" htmlFor={`filter-${key}`}>
                  {label}
                </label>
                <input
                  id={`filter-${key}`}
                  placeholder={placeholder}
                  value={filters[key] || ""}
                  onChange={(e) => change(key, e.target.value)}
                />
              </details>
            ))}
            <details>
              <summary>Seniority</summary>
              <select
                aria-label="Seniority"
                value={filters.seniority || ""}
                onChange={(e) => change("seniority", e.target.value)}
              >
                <option value="">Any seniority</option>
                {[
                  "entry",
                  "mid",
                  "senior",
                  "director",
                  "vp",
                  "ceo",
                  "founder",
                ].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </details>
            <details>
              <summary>Email status</summary>
              <select
                aria-label="Email status"
                value={filters.verification_status || ""}
                onChange={(e) => change("verification_status", e.target.value)}
              >
                <option value="">Any status</option>
                <option value="verified">Verified (imported status)</option>
                <option value="catch-all">Catch-all</option>
                <option value="unknown">Unverified</option>
              </select>
            </details>
            {!publicMode && (
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={filters.unlocked === "false"}
                  onChange={(e) =>
                    change("unlocked", e.target.checked ? "false" : "")
                  }
                />
                Hide already unlocked
              </label>
            )}
            <button className="button button-dark full" disabled={loading}>
              Apply filters
            </button>
          </form>
        </aside>
        <section className="search-results">
          <div className="results-toolbar">
            <div>
              <button
                className="button button-outline mobile-filter"
                onClick={() => setFilterOpen(true)}
              >
                ☷ Filters
              </button>
              <strong>
                {loading ? "Searching…" : `${total.toLocaleString()} contacts`}
              </strong>
              <span className="muted">
                {publicMode ? "Preview · up to 5 records" : "in your results"}
              </span>
            </div>
            <select
              aria-label="Sort contacts"
              value={params.get("sort") || "company"}
              onChange={(e) => {
                const p = new URLSearchParams(params);
                p.set("sort", e.target.value);
                p.set("page", "1");
                setParams(p);
              }}
            >
              <option value="company">Company A–Z</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
          <div className="active-filters">
            {Object.entries(Object.fromEntries(params))
              .filter(
                ([k, v]) => !["page", "pageSize", "sort"].includes(k) && v,
              )
              .map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => {
                    const p = new URLSearchParams(params);
                    p.delete(k);
                    p.set("page", "1");
                    setParams(p);
                  }}
                >
                  {k.replace(/_/g, " ")}: {v} <span>×</span>
                </button>
              ))}
          </div>
          {selected.length > 0 && (
            <div className="selection-bar">
              <strong>{selected.length} selected</strong>
              <button
                className="button button-dark"
                disabled={
                  !leads.some((l) => selected.includes(l.id) && !l.unlocked)
                }
                onClick={() =>
                  requestUnlock(leads.filter((l) => selected.includes(l.id)))
                }
              >
                Unlock selected ·{" "}
                {
                  leads.filter((l) => selected.includes(l.id) && !l.unlocked)
                    .length
                }{" "}
                credits
              </button>
              <select
                aria-label="Choose a list"
                value={listId}
                onChange={(e) => setListId(e.target.value)}
              >
                <option value="">Add to list…</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
              <button
                className="button button-outline"
                disabled={!listId || busy}
                onClick={() => void addToList()}
              >
                Add
              </button>
              {!lists.length && <Link to="/my-leads">Create a list ↗</Link>}
            </div>
          )}
          {loading ? (
            <div className="skeleton-table" aria-label="Loading contacts">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} />
              ))}
            </div>
          ) : leads.length ? (
            <div className="contact-table-wrap">
              <table className="contact-table">
                <thead>
                  <tr>
                    {!publicMode && (
                      <th>
                        <input
                          aria-label="Select all contacts on this page"
                          type="checkbox"
                          checked={
                            leads.length > 0 && selected.length === leads.length
                          }
                          onChange={(e) =>
                            setSelected(
                              e.target.checked ? leads.map((l) => l.id) : [],
                            )
                          }
                        />
                      </th>
                    )}
                    <th>CONTACT</th>
                    <th>COMPANY</th>
                    <th>LOCATION</th>
                    <th>AVAILABLE DATA</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id}>
                      {!publicMode && (
                        <td className="select-cell">
                          <input
                            type="checkbox"
                            aria-label={`Select ${l.first_name} ${l.last_name}`}
                            checked={selected.includes(l.id)}
                            onChange={(e) =>
                              setSelected((current) =>
                                e.target.checked
                                  ? [...current, l.id]
                                  : current.filter((id) => id !== l.id),
                              )
                            }
                          />
                        </td>
                      )}
                      <td>
                        <button
                          className="contact-name"
                          onClick={() => setDrawer(l)}
                        >
                          <span className="contact-avatar">
                            {l.first_name[0]}
                            {l.last_name[0]}
                          </span>
                          <span>
                            <strong>
                              {l.first_name} {l.last_name}
                            </strong>
                            <small>{l.job_title || "Business contact"}</small>
                          </span>
                        </button>
                      </td>
                      <td>
                        <strong>{l.company}</strong>
                        <small>{l.industry || "Industry not provided"}</small>
                      </td>
                      <td>
                        <span>
                          {[l.city, l.country].filter(Boolean).join(", ") ||
                            "Not provided"}
                        </span>
                        <small>
                          {l.company_size ? `${l.company_size} employees` : ""}
                        </small>
                      </td>
                      <td>
                        <span
                          className={`data-badge ${l.verification_badge === "Verified" ? "good" : ""}`}
                        >
                          {l.verification_badge}
                        </span>
                        <small>
                          {l.unlocked
                            ? l.email
                            : `Email ${l.email_available ? "available" : "not provided"}${l.phone_available ? " · Phone available" : ""}`}
                        </small>
                      </td>
                      <td>
                        <button
                          className={`button ${l.unlocked ? "button-outline" : "button-dark"} contact-action`}
                          onClick={() =>
                            l.unlocked ? setDrawer(l) : requestUnlock([l])
                          }
                        >
                          {l.unlocked ? "View details" : "Unlock · 1 credit"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <span>⌕</span>
              <h2>{error ? "Search unavailable" : "No matches yet"}</h2>
              <p>
                {error
                  ? "Please try again in a moment."
                  : "Try a broader role, company, or location. New records will appear here as the database grows."}
              </p>
              <button
                className="button button-outline"
                onClick={() => apply({})}
              >
                Clear filters
              </button>
            </div>
          )}
          <div className="pagination">
            <span>
              {publicMode
                ? "Contact details are masked until unlocked."
                : `Page ${page} of ${Math.max(1, pages)} · 20 contacts per page`}
            </span>
            {!publicMode && (
              <div>
                <button
                  className="button button-outline"
                  disabled={page <= 1 || loading}
                  onClick={() => {
                    const p = new URLSearchParams(params);
                    p.set("page", String(page - 1));
                    setParams(p);
                  }}
                >
                  ← Previous
                </button>
                <button
                  className="button button-outline"
                  disabled={page >= pages || loading}
                  onClick={() => {
                    const p = new URLSearchParams(params);
                    p.set("page", String(page + 1));
                    setParams(p);
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
          {publicMode && (
            <div className="preview-cta">
              <div>
                <strong>Ready to go beyond the preview?</strong>
                <p>
                  Create an account, choose a plan, and unlock your next
                  connection.
                </p>
              </div>
              <Link className="button button-dark" to="/signup">
                Get started ↗
              </Link>
            </div>
          )}
        </section>
      </div>
      {drawer && (
        <ContactDrawer
          lead={drawer}
          publicMode={publicMode}
          onClose={() => setDrawer(null)}
          onUnlock={(l) => requestUnlock([l])}
        />
      )}{" "}
      {!!confirm.length && (
        <div className="modal-overlay">
          <section
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unlock-heading"
          >
            <span className="modal-icon">⌑</span>
            <h2 id="unlock-heading">
              Unlock{" "}
              {confirm.length === 1
                ? "this contact"
                : `${confirm.length} contacts`}
              ?
            </h2>
            <p>
              Spend{" "}
              <strong>
                {confirm.length} credit{confirm.length === 1 ? "" : "s"}
              </strong>{" "}
              to reveal all available details. Reopening and exporting these
              contacts won't cost extra credits.
            </p>
            <div className="modal-balance">
              <span>Your available balance</span>
              <strong>{user?.credits.toLocaleString()} credits</strong>
            </div>
            {user?.subscription_status !== "active" ? (
              <p className="notice">
                Choose a subscription to unlock contacts.{" "}
                <Link to="/pricing">View plans →</Link>
              </p>
            ) : (user?.credits || 0) < confirm.length ? (
              <p className="notice error">
                You don't have enough credits for this selection.
              </p>
            ) : null}
            <div className="modal-actions">
              <button
                className="button button-outline"
                onClick={() => setConfirm([])}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="button button-dark"
                onClick={() => void unlock()}
                disabled={
                  busy ||
                  user?.subscription_status !== "active" ||
                  (user?.credits || 0) < confirm.length
                }
              >
                {busy
                  ? "Unlocking…"
                  : `Confirm · ${confirm.length} credit${confirm.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </section>
        </div>
      )}
      {saveOpen && (
        <div className="modal-overlay">
          <form
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-search-heading"
            onSubmit={(e) => {
              e.preventDefault();
              void saveSearch();
            }}
          >
            <h2 id="save-search-heading">Save this search</h2>
            <p>Return to these filters from your saved searches.</p>
            <label htmlFor="search-name">Search name</label>
            <input
              id="search-name"
              autoFocus
              required
              maxLength={80}
              placeholder="e.g. UK marketing directors"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
            <div className="modal-actions">
              <button
                type="button"
                className="button button-outline"
                onClick={() => setSaveOpen(false)}
              >
                Cancel
              </button>
              <button className="button button-dark" disabled={busy}>
                {busy ? "Saving…" : "Save search"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
  return publicMode ? (
    <div className="public-site">
      <PublicNav />
      <main className="explore-page">{content}</main>
      <PublicFooter />
    </div>
  ) : (
    content
  );
}
